import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarsListPane } from '@/components/settings/calendars/calendars-list-pane';
import { CalendarEditorPane } from '@/components/settings/calendars/calendar-editor-pane';
import { DeleteCalendarDialog } from '@/components/settings/calendars/delete-calendar-dialog';
import { invalidateCalendarsCache } from '@/components/pages/calendar/calendar-data';
import {
  deleteAdminCalendar,
  fetchAdminCalendar,
  fetchAdminCalendars,
} from '@/lib/api/admin-calendars';
import { fetchDoctors } from '@/lib/api/users';
import { fetchTenants } from '@/lib/api/tenants';
import {
  deleteCascadeSummary,
  doctorsToOptions,
  isPlatoCalendar,
  makeCalendarDraft,
  normalizeCalendar,
} from '@/lib/calendars';
import { tenantsToClinicOptions } from '@/lib/queues';
import { publishCalendarRefresh } from '@/lib/realtime/appointment-realtime-bridge';
import { appTimeZone } from '@/lib/timezone';

// The always-mounted Calendar page caches the calendars index for hours and builds its
// pickers/columns from it, so every mutation here drops that cache and pings the refresh bus.
function calendarsChanged() {
  invalidateCalendarsCache();
  publishCalendarRefresh({ source: 'calendars-changed' });
}

// Settings → SCHEDULING → Calendars: the list ↔ single-calendar editor flow. The list comes from
// the admin endpoint (with usage counts); the editor POSTs on Create / PUTs on Save and the
// sub-page patches its list from the response. Delete lives on the list behind a cascade
// confirm that refreshes the usage counts on open.
export function CalendarsSubPage({ canEdit = true }) {
  const [calendars, setCalendars] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [clinicOptions, setClinicOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: 'list' });
  // { calendar, usage, loadingUsage } while the confirm is open.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminCalendars({ includeUsage: true })
      .then((items) => {
        if (cancelled) return;
        setCalendars(items.map(normalizeCalendar).filter((c) => c.id));
        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoading(false);
        toast.error(error instanceof Error ? error.message : 'Could not load calendars');
      });
    // Rosters only label chips and fill the owner pickers; a failure never blocks the list
    // (labels fall back to raw ids).
    fetchDoctors()
      .then((res) => {
        if (cancelled) return;
        setDoctorOptions(doctorsToOptions(res?.items));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('calendars editor: failed to load doctors', error);
        toast.error(error instanceof Error ? `Could not load doctors: ${error.message}` : 'Could not load doctors');
      });
    fetchTenants()
      .then((res) => {
        if (cancelled) return;
        setClinicOptions(tenantsToClinicOptions(res?.items));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('calendars editor: failed to load clinics (tenants)', error);
        toast.error(error instanceof Error ? `Could not load clinics: ${error.message}` : 'Could not load clinics');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCreate = () => {
    if (!canEdit) return;
    setView({ mode: 'create', draft: makeCalendarDraft({ timezone: appTimeZone() }) });
  };

  const handleEdit = (id) => {
    const calendar = calendars.find((c) => c.id === id);
    if (calendar) setView({ mode: 'edit', calendar });
  };

  // Create and edit both land here with the server's document: patch (or append to) the list,
  // wake the Calendar page, and turn a just-created draft into a regular edit session.
  const handleSaved = (saved) => {
    setCalendars((prev) => (prev.some((c) => c.id === saved.id) ? prev.map((c) => (c.id === saved.id ? saved : c)) : [...prev, saved]));
    calendarsChanged();
    setView((prev) => (prev.mode === 'create' ? { mode: 'edit', calendar: saved } : prev));
  };

  const handleDeleteRequest = (calendar) => {
    if (!canEdit || !calendar || isPlatoCalendar(calendar) || deletingId) return;
    setDeleteTarget({ calendar, usage: calendar.usage, loadingUsage: true });
    fetchAdminCalendar({ calendarId: calendar.id })
      .then((res) => {
        const fresh = normalizeCalendar(res);
        setDeleteTarget((prev) =>
          prev && prev.calendar.id === calendar.id ? { ...prev, usage: fresh.usage, loadingUsage: false } : prev,
        );
      })
      .catch((error) => {
        console.error('calendars editor: failed to refresh usage before delete', error);
        // Fall back to the counts the list already had.
        setDeleteTarget((prev) => (prev && prev.calendar.id === calendar.id ? { ...prev, loadingUsage: false } : prev));
      });
  };

  const handleDeleteConfirm = async () => {
    const target = deleteTarget?.calendar;
    if (!target || deletingId) return;
    setDeletingId(target.id);
    try {
      const res = await deleteAdminCalendar({ calendarId: target.id });
      setCalendars((prev) => prev.filter((c) => c.id !== target.id));
      calendarsChanged();
      setDeleteTarget(null);
      setView((prev) => (prev.mode === 'edit' && prev.calendar.id === target.id ? { mode: 'list' } : prev));
      toast.success(deleteCascadeSummary(res?.cascade));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete calendar');
    } finally {
      setDeletingId('');
    }
  };

  if (view.mode === 'create' || view.mode === 'edit') {
    return (
      <CalendarEditorPane
        key={view.mode === 'edit' ? `edit:${view.calendar.id}` : 'create'}
        mode={view.mode}
        initialCalendar={view.mode === 'edit' ? view.calendar : null}
        initialDraft={view.mode === 'create' ? view.draft : null}
        canEdit={canEdit}
        doctorOptions={doctorOptions}
        clinicOptions={clinicOptions}
        onBack={() => setView({ mode: 'list' })}
        onSaved={handleSaved}
      />
    );
  }

  return (
    <>
      <CalendarsListPane
        calendars={calendars}
        loading={loading}
        canEdit={canEdit}
        doctorOptions={doctorOptions}
        clinicOptions={clinicOptions}
        deletingId={deletingId}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDeleteRequest}
      />
      <DeleteCalendarDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deletingId) setDeleteTarget(null);
        }}
        calendar={deleteTarget?.calendar || null}
        usage={deleteTarget?.usage || null}
        loadingUsage={Boolean(deleteTarget?.loadingUsage)}
        deleting={Boolean(deletingId)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
